import {defineStore} from 'pinia';
import {useContextStore} from './contextStore';
import {useWebSocketFactoryCacheStore} from '@/stores/global/webSocketFactoryCacheStore';
import commonFunction from '@/commons/commonFunction';
import WebSocketEvent from '@/commons/wildcards/constants/WebSocketEvent';
import fileApi from '@/apis/fl/fileApi';
import StorageFileType from '@/commons/wildcards/constants/StorageFileType';
import exportApi from '@/apis/ex/exportApi';
import {MSConstant} from '@/commons/wildcards';
import {msFormat} from '@/commons/msFormat';
import commonFn from '@/commons/commonFunction';
import popupUtil from '@/commons/popupUtil.js';
import {useDataManagementStore} from '@/stores/dataManagement/dataManagementStore';
import EventBusGlobal, {GlobalEventName} from '@/commons/eventBusGlobal';
import {showAlert} from '@/commons/globalMessage';
import notificationApi from '@/apis/other/notificationApi';
import {usePostedBatch} from '@/stores/postBatch/postBatchStore';
import {usePostedMulti} from '@/stores/postMulti/postMultiStore';
import {useWebSocketFactoryNotificationStore} from '@/stores/global/webSocketFactoryNotificationStore';
// api
import DVCIntergrationAPI from '@/apis/dvc/dvcIntergrationApi.js';
import {$vfm} from 'vue-final-modal';
// commons
import {useDvcCommon} from '@/views/etreasury/dvcCommon.js';

export const useGlobalSocketStore = defineStore('GlobalSocket', {
  state: () => {
    return {mapSession: {}, mapTimeInterval: {}, connectedSocket: false, sessionId: null};
  },
  actions: {
    updateConnectedSocket(sessionId) {
      this.connectedSocket = true;
      this.sessionId = sessionId;
    },
    [WebSocketEvent.InvalidCacheDi]: args => {
      const messageData = JSON.parse(args);
      if (messageData) {
        const context = useContextStore();
        if (context.context.BambooSessionId !== messageData.client && messageData.module) {
          const store = useWebSocketFactoryCacheStore().factoryCacheStore(messageData.module);
          if (store) {
            store.loadFullCache({remoteVersion: messageData.version});
          }
        }
      }
    },

    [WebSocketEvent.PostBatchProcess]: async function (args) {
      const message = JSON.parse(args);

      if (message) {
        usePostedBatch().setMessage(message);
      }
    },

    // socket cho ghi sổ nhiều chứng từ
    [WebSocketEvent.PostMultiProcess]: async function (args) {
      const message = JSON.parse(args);

      if (message) {
        usePostedMulti().setMessage(message);
      }
    },

    [WebSocketEvent.ExportList]: function (args) {
      const {Data, ActionId} = JSON.parse(args);
      if (this.mapSession[WebSocketEvent.ExportList] !== ActionId) return;
      const intervalId = this.mapTimeInterval[WebSocketEvent.ExportList];
      clearInterval(intervalId);

      const link = fileApi.getFilePreviewLink(StorageFileType.Temp, `${Data.fileName}`, Data.name);
      commonFunction.downloadLink(link);

      //Thêm event bus ẩn toast
      EventBusGlobal.$emit(GlobalEventName.clearToast);
    },

    /**
     * Xuất khẩu XML theo websocket
     * HQMINH 05.09.2023
     */
    [WebSocketEvent.ExportXML]: function (args) {
      const {Data, ActionId} = JSON.parse(args);
      if (this.mapSession[WebSocketEvent.ExportXML] !== ActionId) return;
      const intervalId = this.mapTimeInterval[WebSocketEvent.ExportXML];
      clearInterval(intervalId);

      const link = fileApi.getFilePreviewLink(StorageFileType.Temp, `${Data.fileName}`, Data.name);
      commonFunction.downloadLink(link);

      //Thêm event bus ẩn toast
      EventBusGlobal.$emit(GlobalEventName.clearToast);
    },

    /**
     * Xuất khẩu Json theo websocket
     * HQMINH 27.09.2023
     */
    [WebSocketEvent.ExportJson]: function (args) {
      const {Data, ActionId} = JSON.parse(args);
      if (this.mapSession[WebSocketEvent.ExportJson] !== ActionId) return;
      const intervalId = this.mapTimeInterval[WebSocketEvent.ExportJson];
      clearInterval(intervalId);

      const link = fileApi.getFilePreviewLink(StorageFileType.Temp, `${Data.fileName}`, Data.name);
      commonFunction.downloadLink(link);

      //Thêm event bus ẩn toast
      EventBusGlobal.$emit(GlobalEventName.clearToast);
    },

    // Cập nhật layout template
    [WebSocketEvent.UpdateLayoutTemplate]: function (args) {
      const {Data, ActionId} = JSON.parse(args);
      if (Data && Data.length > 0) {
        const context = useContextStore();
        const userID = context?.context?.Context?.UserId;
        if (userID) {
          Data.forEach(x => {
            commonFn.removeSessionStorage('LayoutTemplate_{0}_{1}'.format(x, userID));
          });
        }
      }
    },

    /**
     * Admin đã xóa người dùng === chính bạn -> logout
     * @param {*} args
     * nvxuan 04.10.2023
     */
    [WebSocketEvent.DeleteSysUser]: function (args) {
      const {user_id} = JSON.parse(args);
      const context = useContextStore();
      const curUserID = context?.context?.Context?.UserId;

      if (curUserID && curUserID == user_id) {
        commonFn.logoff();
      }
    },

    /**
     * Admin đã thay đổi vai trò của bạn, bạn cần load lại vai trò
     * @param {*} args
     * nvxuan 04.10.2023
     */
    [WebSocketEvent.ChangeRoleOfSysUser]: async function (args) {
      const {user_id, exe_user_id, affectUserIds} = JSON.parse(args);
      const context = useContextStore();
      const curUserID = context?.CurUserId;
      const payload = {
        databaseId: context?.CurDatabaseId,
        tenantId: context?.context?.Context?.TenantId,
        userId: curUserID,
      };

      if ((user_id && curUserID == user_id) || (affectUserIds && affectUserIds.contains(curUserID))) {
        const token = await context.getToken(payload);

        // Nếu chính mình thay đổi vai trò của mình thì reload luôn không cần thông báo
        if (exe_user_id == curUserID) {
          location.reload();
        } else {
          // show thông báo
          showAlert('Quản trị dữ liệu đã thay đổi vai trò quyền hạn của bạn.', 'Quyền hạn thay đổi').then(() => {
            location.reload();
          });
        }
      }
    },

    /**
     * Thông báo bắn về
     * @param {*} sender
     * @param {*} res
     * tvtoan 2.6.2023
     */
    notification: args => {
      const messageData = JSON.parse(args);
      // 5: khóa sổ
      if (messageData.type == 5) {
        const errorDataCloseBook = useWebSocketFactoryNotificationStore().convertData(messageData);
      }
    },

    [WebSocketEvent.NewNotification]: function (args) {
      const context = useContextStore();
      const curUserID = context?.CurUserId;
      const notiData = JSON.parse(args)?.Data;
      const {affect_user_id, target} = notiData;
      let isValid = false;

      // check đối tượng nhận thông báo
      // target
      //    0.All.Tất cả
      //    1.User.Thông báo cho user
      //    2.UserWithDB.Thông báo cho user của một database
      //    3.Database.Thông báo cho tất cả người dùng trong database
      if ([0, 3].includes(target) || ([1, 2].includes(target) && affect_user_id == curUserID)) {
        isValid = true;
      }

      if (isValid) {
        // Lấy lại số lượng thông báo chưa đọc hiển thị trên TopNav
        commonFn.refreshNotiBell();
      }
    },

    /**
     * Tự động cập nhật lại dữ liệu danh sách hồ sơ khi có sự thay đổi sau khi lấy danh sách từ DVC về
     * @param {*} args
     * nvthinh 9.11.2023
     */
    [WebSocketEvent.UpdateDvcList]: function (args) {
      //Thêm event bus reload màn DVC
      EventBusGlobal.$emit(GlobalEventName.gridReloadDVC);
    },

    /**
     * Emit sự kiện lấy kết quả sau khi thực hiện sinh CT từ CT salagov
     * @param {Object} response
     * nvthinh 22.1.2023
     */
    [WebSocketEvent.GenerateVoucherFromSalagov]: function (response) {
      EventBusGlobal.$emit(GlobalEventName.generateVoucherFromSalagov, response);
    },

    /**
     * Emit sự kiện lấy kết quả sau khi thực hiện Kết chuyển nhanh
     * @param {Object} response
     * nvthinh 29.01.2023
     */
    [WebSocketEvent.QuickTransfer]: function (response) {
      EventBusGlobal.$emit(GlobalEventName.quickTransfer, response);
    },

    /**
     * Emit sự kiện tiến trình sao chép dữ liệu
     * @param {Object} response
     * nvthinh 29.01.2023
     */
    [WebSocketEvent.CopyData]: function (response) {
      EventBusGlobal.$emit(GlobalEventName.copyData, response);
    },

    /**
     * @description Emit sự kiện lấy kết quả sau khi lưu xong dữ liệu dashboard
     * @param {Object} response
     * @author NXHung 21.05.2024
     */
    [WebSocketEvent.DashboardData]: function (response) {
      EventBusGlobal.$emit(GlobalEventName.dashboardData, response);
    },
    /**
     * Emit sự kiện reload lại màn hóa đơn mua hàng sau khi đồng bộ xong dữ liệu
     * kbnguyet 25.06.2024
     * @param {*} response
     */

    [WebSocketEvent.SyncVoucherFromInbot]: function (response) {
      // thêm event bus reload lại màn đồng bộ hóa đơn
      EventBusGlobal.$emit(GlobalEventName.gridReloadInbotList, response);
    },

    /**
     * Emit sự kiện reload lại màn hóa đơn mua hàng lần đầu sau khi đồng bộ xong dữ liệu
     * kbnguyet 25.06.2024
     * @param {*} response
     */

    [WebSocketEvent.SyncVoucherFromInbotFirstTime]: function (response) {
      // thêm event bus reload lại màn đồng bộ hóa đơn
      EventBusGlobal.$emit(GlobalEventName.gridReloadInbotListFirstTime, response);
    },

    /**
     * Thông báo bắn về
     * @param {*} sender
     * @param {*} res
     * tvtoan 2.6.2023
     */
    convert: args => {
      const messageData = JSON.parse(args);
      const message = JSON.parse(messageData.Message);
      const date = new Date(message.CreatedDate).getDate();
      const month = new Date(message.CreatedDate).getMonth() + 1;
      const monthfixed = month < 10 ? '0' + month : month;
      const year = new Date(message.CreatedDate).getFullYear();
      message.createdDate = `${date}/${monthfixed}/${year}`;
      useDataManagementStore().setMessage(message);
    },

    endConvert: args => {
      const messageData = JSON.parse(args);
      const message = JSON.parse(messageData.Message);
      const date = new Date(message.CreatedDate).getDate();
      const month = new Date(message.CreatedDate).getMonth() + 1;
      const monthfixed = month < 10 ? '0' + month : month;
      const year = new Date(message.CreatedDate).getFullYear();
      message.createdDate = `${date}/${monthfixed}/${year}`;
      useDataManagementStore().setMessage(message);
    },

    /**
     * Bắt đầu sao lưu dữ liệu
     * nvxuan 20.11.2023
     * Backup send by dbid
     */
    [WebSocketEvent.BackupDataStart]: args => {
      const messageData = JSON.parse(args)?.Data,
        context = useContextStore(),
        curUserID = context?.CurUserId,
        keyFlag = '_ShowBackupProcesss';

      messageData?.BackupParam &&
        messageData?.BackupDataRecord &&
        !window[keyFlag] &&
        popupUtil.show('BackupRestoreProcessPopupX', {
          BackupDataRecord: messageData.BackupDataRecord,
        });

      delete window[keyFlag];
    },

    /**
     * Hủy sao lưu dữ liệu
     * nvxuan 20.11.2023
     */
    [WebSocketEvent.BackupDataCancel]: args => {
      const messageData = JSON.parse(args);

      // Hủy sao lưu dữ liệu
      EventBusGlobal.$emit(GlobalEventName.backupDataCancel, {
        backup_database_id: messageData.Data.BackupDataRecord.backup_database_id,
      });

      // reload lại ds Quản lý dữ liệu
      EventBusGlobal.$emit(GlobalEventName.reloadListDbManagement);

      // reload lại danh sách sao lưu & khôi phục nếu đang mở
      EventBusGlobal.$emit(GlobalEventName.reloadListBackupRestore);
    },

    /**
     * Sau khi backup dữ liệu kết thúc
     * nvxuan 20.11.2023
     */
    [WebSocketEvent.BackupDataCompleted]: async args => {
      const messageData = JSON.parse(args).Data;

      // Do người dùng thực hiện ở 1 db nhưng trong quá trình chuyển sang DB khác nên cần insert thông báo ở db đang đứng
      messageData?.CacheNotiId && (await notificationApi.tryInsertNoti(messageData.CacheNotiId));

      // Kết thúc backup
      messageData && EventBusGlobal.$emit(GlobalEventName.backupDataCompleted, messageData);
    },

    /**
     * Sau khi khôi phục dữ liệu kết thúc
     * nvxuan 22.11.2023
     */
    [WebSocketEvent.RestoreDataCompleted]: args => {
      const messageData = JSON.parse(args).Data;

      // Do người dùng thực hiện ở 1 db nhưng trong quá trình chuyển sang DB khác nên cần insert thông báo ở db đang đứng

      // Kết thúc backup
      messageData && EventBusGlobal.$emit(GlobalEventName.restoreDataCompleted, messageData);
    },

    /**
     * Bắt đầu bảo trì dữ liệu
     * nvxuan 08.12.2023
     */
    [WebSocketEvent.DataMaintainStart]: args => {
      const messageData = JSON.parse(args).Data,
        context = useContextStore(),
        curUserID = context?.CurUserId,
        curDbID = context?.CurDatabaseId,
        taskRecord = messageData?.TaskManagementParam,
        {openedModals} = $vfm,
        isOpen =
          Array.isArray(openedModals) &&
          openedModals.length > 0 &&
          openedModals.filter(e => e?.params?._rawValue?.name == 'DataMantainance').length > 0;

      taskRecord &&
        taskRecord.database_id == curDbID &&
        (taskRecord.user_id != curUserID || !isOpen) &&
        popupUtil.show('DataMantainance', {
          callFrom: 'MainLayout',
          TaskManagementParam: taskRecord,
        });
    },

    /**
     * tạo dữ liệu mới từ dữ liệu cũ
     * tvtoan 10.01.2024
     */
    [WebSocketEvent.CreateDataFromOldDataComplete]: args => {
      const messageData = JSON.parse(args)?.Data;
      // Kết thúc tạo dữ liệu mới từ dữ liệu cũ
      messageData && EventBusGlobal.$emit(GlobalEventName.createDataFromOldDataComplete, messageData);
    },

    /**
     * tạo dữ liệu mới từ dữ liệu cũ
     * tvtoan 10.01.2024
     */
    [WebSocketEvent.CreateDataFromOldDataStart]: args => {
      const messageData = JSON.parse(args)?.Data;
      // Kết thúc tạo dữ liệu mới từ dữ liệu cũ
      messageData && EventBusGlobal.$emit(GlobalEventName.createDataFromOldDataStart, messageData);
    },

    /**
     * lấy dữ liệu kết quả kiểm tra định mức
     * vqphong - 26/09/2024
     */
    [WebSocketEvent.CheckExpenseNormRegulation]: args => {
      const messageData = JSON.parse(args)?.Data;
      messageData && EventBusGlobal.$emit(GlobalEventName.checkExpenseNormRegulation, messageData);
    },

    /**
     * Khôi phục chứng từ thành công
     * @param {*} args
     */
    [WebSocketEvent.RestoreVoucherSuccess]: async args => {
      const messageData = JSON.parse(args).Data;

      // Nếu đang mở màn hình Danh sách chứng từ đã xóa thì reload lại
      // Nếu đang mở Nhật ký truy cập thì update lại trạng thái Khôi phục
      messageData?.vdid && EventBusGlobal.$emit(GlobalEventName.restoreVoucherSuccess, messageData);
    },

    /**
     * Xuất khẩu danh sách
     * @param {*} exportParam: Param xuất khẩu
     */
    async exportList(exportParam) {
      this.processData(exportParam.ColumnExports, exportParam.Data);
      const sessionId = await exportApi.exportListAsync(exportParam);
      this.mapSession[WebSocketEvent.ExportList] = sessionId;

      // Tên file tải về
      let fileDownName = exportParam && exportParam.FileName ? `${exportParam.FileName}.xlsx` : null;

      //Khởi tạo 1 check pull lên server
      this.mapTimeInterval[WebSocketEvent.ExportList] = commonFunction.createIntervalCheckPull(
        window._appConfig.totalCheckPull.exportList,
        window._appConfig.timeIntervalPull.exportList,
        `${sessionId}.xlsx`,
        fileDownName,
      );
    },

    /**
     * Xuất khẩu tình hình sử dụng
     * NVXUAN 19.10.2023
     */
    async exportMonitorUsageData() {
      const fileDownName = `Theo dõi tình hình sử dụng`;
      const sessionId = await exportApi.exportMonitorUsageDataAsync({
        FileName: fileDownName,
      });
      this.mapSession[WebSocketEvent.ExportList] = sessionId;

      // Tên file tải về

      //Khởi tạo 1 check pull lên server
      this.mapTimeInterval[WebSocketEvent.ExportList] = commonFunction.createIntervalCheckPull(
        window._appConfig.totalCheckPull.exportList,
        window._appConfig.timeIntervalPull.exportList,
        `${sessionId}.xlsx`,
        `${fileDownName}.xlsx`,
      );
    },

    /**
     * Xuất khẩu báo cáo tổng hợp tài chính
     * @param {*} exportParam: Param xuất khẩu
     * NVXUAN 03.08.2023
     */
    async exportSumFinanceReportAsync(exportParam) {
      // Thực hiện xuất khẩu
      const sessionId = await exportApi.exportSumFinanceReportAsync(exportParam);

      this.mapSession[WebSocketEvent.ExportList] = sessionId;

      // Tên file tải về
      let fileDownName = exportParam && exportParam.FileName ? `${exportParam.FileName}.xlsx` : null;

      //Khởi tạo 1 check pull lên server
      this.mapTimeInterval[WebSocketEvent.ExportList] = commonFunction.createIntervalCheckPull(
        window._appConfig.totalCheckPull.exportList,
        window._appConfig.timeIntervalPull.exportList,
        `${sessionId}.xlsx`,
        fileDownName,
      );
    },

    /**
     * Xuất khẩu XML
     * HQMINH 05.09.2023
     */
    async exportXMLAsync(res, param) {
      const me = this;
      me.mapSession[WebSocketEvent.ExportXML] = res;

      // Tên file tải về
      let fileDownName = res && res.FileName ? `${res.FileName}.zip` : null;

      //Khởi tạo 1 check pull lên server
      this.mapTimeInterval[WebSocketEvent.ExportXML] = commonFn.createIntervalCheckPull(
        window._appConfig.totalCheckPull.exportXML,
        window._appConfig.timeIntervalPull.exportXML,
        `${res.Id}.zip`,
        fileDownName,
      );
    },

    /**
     * Xuất khẩu Json
     * HQMINH 07.09.2023
     */
    async exportJsonAsync(res, param) {
      const me = this;
      me.mapSession[WebSocketEvent.ExportJson] = res;

      // Tên file tải về
      let fileDownName = res && res.FileName ? `${res.FileName}.json` : null;

      //Khởi tạo 1 check pull lên server
      this.mapTimeInterval[WebSocketEvent.ExportJson] = commonFn.createIntervalCheckPull(
        window._appConfig.totalCheckPull.exportJson,
        window._appConfig.timeIntervalPull.exportJson,
        `export_${res.Id}.json`,
        fileDownName,
        true,
      );
    },

    /**
     * Process lại dữ liệu export
     * @param {*} columns
     * @param {*} data
     */
    processData(columns, data) {
      columns = columns.filter(x => x.dataField !== 'rowAction' && x.dataField !== 'rowState' && x.visible !== false);
      const colFormatEnums = columns.filter(x => x.formatType === MSConstant.FormatType.Enum);

      if (colFormatEnums.length > 0) {
        data.forEach(item => {
          colFormatEnums.forEach(col => {
            if (col.enum) {
              // sửa lỗi format enum nhhoang1 20.07.2023
              const text = msFormat.formatEnum(item[col.dataField], col.enum);

              item[`${col.dataField}_enum`] = text;
            }
          });
        });
      }
    },

    /**
     * Kích hoạt sự kiện call 5' lần (Gọi lên DVC để cập nhật trạng thái cho dữ liệu hiện tại)
     * Khi cập nhật trạng thái xong => Server tự động bắn websocket về cập nhật lại màn danh sách
     * @author nvthinh 28.11.2023
     */
    triggerEventDVC() {
      const me = this;
      const key = WebSocketEvent.GetDocumentStatusDvc;
      const timeIntervalPull = window._appConfig.timeIntervalPull.getDocumentStatusDvc;
      const dvcToken = commonFn.getLocalStorage('DvcToken'); // Lấy token dvc từ local storage
      const dvcUser = JSON.parse(commonFn.getLocalStorage('DvcUser'), '{}'); // Lấy dvcuser từ local storage
      const {isDvcAuth} = useDvcCommon();

      // Không thực thi nếu không có token dvc hoặc đã tồn tại interval
      const intervalIdTmp = me.mapTimeInterval[key];
      if (!dvcToken || !dvcUser || intervalIdTmp) return;

      let payload = {
        dvcToken: dvcToken,
        budgetCode: dvcUser.budget_code,
      };

      // clear interval (nvthinh 11.04.2024)
      const fn = () => {
        const intervalId = me.mapTimeInterval[key];
        clearInterval(intervalId);
      };

      // Call API lần đầu tiên
      DVCIntergrationAPI.triggerEvent(payload)
        .then(res => {
          if (res) {
            // Sau khi call API lần đầu thành công => Thực hiện setInterval
            let intervalId = setInterval(() => {
              // Kiểm tra phiên đăng nhập DVC còn không
              const isValid = isDvcAuth();
              // Nếu còn token dvc thì mới kích hoạt sự kiện
              if (isValid) {
                try {
                  DVCIntergrationAPI.triggerEvent(payload);
                } catch (error) {
                  // Nếu gặp lỗi => clear interval
                  fn();
                  console.log(error);
                }
              } else {
                // Nếu không clear interval
                fn();
              }
            }, timeIntervalPull);
            me.mapTimeInterval[key] = intervalId;
          }
        })
        .catch(ex => {
          console.log(ex);
        });
    },

    /**
     * Hủy bỏ Interval theo key
     * @param {String} key
     * @author nvthinh 28.11.2023
     */
    clearIntervalByKey(key) {
      const me = this;

      const intervalId = me.mapTimeInterval[key];
      clearInterval(intervalId);
      delete me.mapTimeInterval[key];
    },
  },
});
